import type { Resource } from "@core/shared/types";
import { useAction } from "@framework/shared/hooks/useAction";
import { useCallback, useRef } from "react";
import type { VideoActivityRequest } from "../../types";
import { compressActivityPayload } from "../../utils/payload-compression";

type UseTrackResourceActivitiesReturn = {
  trackVideoActivity: (
    action: VideoActivityRequest["action"],
    currentTime?: number,
  ) => Promise<void>;
};

export function useTrackResourceActivities(
  resource: Resource,
): UseTrackResourceActivitiesReturn {
  const submit = useAction("/activities/resources");

  const trackVideoActivity = useCallback(
    async (action: VideoActivityRequest["action"], currentTime?: number) => {
      try {
        const payload = {
          type: "activity",
          resource: resource.id,
          data: {
            action,
            currentTime,
          },
        };

        const compressedPayload = compressActivityPayload(payload);
        await submit(compressedPayload);
      } catch (error) {
        console.error("Failed to track video activity:", error);
        // Don't throw error to avoid breaking user experience
      }
    },
    [resource.id, submit],
  );

  return {
    trackVideoActivity,
  };
}

export function useTrackVideoCurrentTime(resource: Resource) {
  const submit = useAction("/activities/resources");
  const lastTrackedTimeRef = useRef<number>(0);
  const lastTrackedTimestampRef = useRef<number>(0);

  const trackVideoCurrentTime = async (currentTime: number, options?: { force?: boolean }) => {
    const now = Date.now();
    const timeSinceLastTrack = now - lastTrackedTimestampRef.current;
    const timeDifference = Math.abs(currentTime - lastTrackedTimeRef.current);
    
    // Skip throttling if force is true
    if (!options?.force) {
      // Only track if:
      // 1. At least 5 seconds have passed since last track, OR
      // 2. At least 10 seconds have passed AND there's a significant time difference (5+ seconds)
      const shouldTrack = 
        timeSinceLastTrack >= 5000 || 
        (timeSinceLastTrack >= 10000 && timeDifference >= 5);

      if (!shouldTrack) {
        return;
      }
    }

    // Update tracking references immediately to prevent rapid calls
    lastTrackedTimeRef.current = currentTime;
    lastTrackedTimestampRef.current = now;

    try {
      const payload = {
        type: "sct",
        resource: resource.id,
        data: {
          currentTime,
        },
      };

      const compressedPayload = compressActivityPayload(payload);
      await submit(compressedPayload);
    } catch (error) {
      console.error("Failed to track video current time:", error);
      // Don't throw error to avoid breaking user experience
    }
  };

  return {
    trackVideoCurrentTime,
  };
}
